import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, fromEvent } from 'rxjs';
import { filter, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { SiteCacheService } from '../site-cache/site-cache.service';
import { VersionService } from '../version/version.service';

export interface UpdateNotification {
  type: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  actions: Array<{
    label: string;
    action: () => void;
    primary?: boolean;
  }>;
  persistent?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UpdateNotificationService {
  private siteCacheService = inject(SiteCacheService);
  private versionService = inject(VersionService);

  private notificationSubject = new BehaviorSubject<UpdateNotification | null>(null);
  public notification$ = this.notificationSubject.asObservable();

  private isOnline = navigator.onLine;

  constructor() {
    this.setupUpdateMonitoring();
    this.setupNetworkMonitoring();
  }

  private setupUpdateMonitoring(): void {
    // Monitor for service worker updates
    this.siteCacheService.updateAvailable$.pipe(
      distinctUntilChanged(),
      filter(available => available)
    ).subscribe(() => {
      this.showUpdateAvailableNotification();
    });

    // Monitor for version mismatches
    this.versionService.hasVersionMismatch().pipe(
      distinctUntilChanged(),
      filter(hasMismatch => hasMismatch)
    ).subscribe(() => {
      this.showVersionMismatchNotification();
    });

    // Monitor for unrecoverable states
    this.siteCacheService.unrecoverable$.subscribe(() => {
      this.showCriticalUpdateNotification();
    });
  }

  private setupNetworkMonitoring(): void {
    // Monitor network status changes
    fromEvent(window, 'online').subscribe(() => {
      this.isOnline = true;
      if (this.siteCacheService.isSupported()) {
        // Check for updates when coming back online
        setTimeout(() => {
          this.siteCacheService.checkForUpdate();
        }, 1000);
      }
    });

    fromEvent(window, 'offline').subscribe(() => {
      this.isOnline = false;
    });
  }

  private showUpdateAvailableNotification(): void {
    const notification: UpdateNotification = {
      type: 'info',
      title: 'Update Available',
      message: 'A new version of DUNA is available with improvements and bug fixes.',
      actions: [
        {
          label: 'Update Now',
          action: () => {
            this.applyUpdate();
          },
          primary: true
        },
        {
          label: 'Later',
          action: () => {
            this.dismissNotification();
            // Show again in 30 minutes
            setTimeout(() => {
              this.showUpdateAvailableNotification();
            }, 30 * 60 * 1000);
          }
        }
      ],
      persistent: false
    };

    this.notificationSubject.next(notification);
  }

  private showVersionMismatchNotification(): void {
    const notification: UpdateNotification = {
      type: 'warning',
      title: 'Version Mismatch Detected',
      message: 'There may be a newer version available. We recommend updating to ensure you have the latest features.',
      actions: [
        {
          label: 'Refresh App',
          action: () => {
            this.forceRefresh();
          },
          primary: true
        },
        {
          label: 'Check Version',
          action: () => {
            this.showVersionInfo();
          }
        },
        {
          label: 'Dismiss',
          action: () => {
            this.dismissNotification();
          }
        }
      ],
      persistent: false
    };

    this.notificationSubject.next(notification);
  }

  private showCriticalUpdateNotification(): void {
    const notification: UpdateNotification = {
      type: 'critical',
      title: 'App Update Required',
      message: 'A critical issue has been detected. The app needs to be refreshed to continue working properly.',
      actions: [
        {
          label: 'Refresh Now',
          action: () => {
            this.forceRefresh();
          },
          primary: true
        }
      ],
      persistent: true
    };

    this.notificationSubject.next(notification);
  }

  public async applyUpdate(): Promise<void> {
    try {
      this.showLoadingNotification('Updating application...');
      
      await this.siteCacheService.applyUpdate();
    } catch (error) {
      console.error('[UpdateNotificationService] Error applying update:', error);
      this.showErrorNotification('Failed to update. Trying complete refresh...');
      
      // Fallback to force refresh
      setTimeout(() => {
        this.forceRefresh();
      }, 2000);
    }
  }

  private forceRefresh(): void {
    this.showLoadingNotification('Refreshing application...');
    
    // Give user time to see the message
    setTimeout(() => {
      this.siteCacheService.performCompleteCacheFlush();
    }, 1000);
  }

  private showLoadingNotification(message: string): void {
    const notification: UpdateNotification = {
      type: 'info',
      title: 'Please Wait',
      message,
      actions: [],
      persistent: true
    };

    this.notificationSubject.next(notification);
  }

  private showErrorNotification(message: string): void {
    const notification: UpdateNotification = {
      type: 'warning',
      title: 'Update Error',
      message,
      actions: [
        {
          label: 'Retry',
          action: () => {
            this.forceRefresh();
          },
          primary: true
        }
      ],
      persistent: false
    };

    this.notificationSubject.next(notification);
  }

  private async showVersionInfo(): Promise<void> {
    try {
      const versionInfo = await this.versionService.getVersionInfo();
      
      const message = `
Current Version: ${versionInfo.displayVersion}
Backend Version: ${versionInfo.backendVersion}
Service Worker: ${versionInfo.serviceWorkerVersion}
Update Available: ${versionInfo.updateAvailable ? 'Yes' : 'No'}
Last Checked: ${new Date(versionInfo.timestamp).toLocaleString()}
Online: ${this.isOnline ? 'Yes' : 'No'}
      `.trim();

      alert(message);
    } catch (error) {
      console.error('[UpdateNotificationService] Error getting version info:', error);
      alert('Unable to retrieve version information.');
    }
  }

  public dismissNotification(): void {
    this.notificationSubject.next(null);
    // Reset update available indicator when user dismisses
    this.versionService.resetUpdateAvailable();
  }

  /**
   * Manually trigger update check
   */
  public async checkForUpdates(): Promise<void> {
    
    try {
      const updateFound = await this.siteCacheService.checkForUpdate();
      
      if (!updateFound) {
        // Check for version mismatches if no SW update found
        this.versionService.hasVersionMismatch().subscribe(hasMismatch => {
          if (!hasMismatch) {
            this.showNoUpdatesNotification();
          }
        }).unsubscribe();
      }
    } catch (error) {
      console.error('[UpdateNotificationService] Error checking for updates:', error);
      this.showErrorNotification('Failed to check for updates. Please try again.');
    }
  }

  private showNoUpdatesNotification(): void {
    const notification: UpdateNotification = {
      type: 'info',
      title: 'No Updates Available',
      message: 'You are running the latest version of DUNA.',
      actions: [
        {
          label: 'OK',
          action: () => {
            this.dismissNotification();
          },
          primary: true
        }
      ],
      persistent: false
    };

    this.notificationSubject.next(notification);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      this.dismissNotification();
    }, 3000);
  }

  /**
   * Get current notification status
   */
  public hasActiveNotification(): Observable<boolean> {
    return this.notification$.pipe(
      map(notification => notification !== null)
    );
  }
} 